import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { canApproveExpenses, isEmployee, useSession } from './auth/session';
import { FirstBusiness, PlanRequired, SignIn } from './auth/SignIn';
import { pendingInviteToken } from './auth/invite';
import { pendingPracticeInviteToken } from './auth/practiceInvite';
import { pendingAccountantInviteToken } from './auth/accountantInvite';
import { pendingCheckout } from './auth/checkout';
import { LinkCheckout } from './pages/billing/Subscription';
import { Shell } from './layout/Shell';
import { Loading, PageHead } from './ui/kit';
import { APP_NAME } from './lib/brand';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Invoices = lazy(() => import('./pages/Invoices'));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'));
const RecurringList = lazy(() => import('./pages/recurring/RecurringList'));
const RecurringDetail = lazy(() => import('./pages/recurring/RecurringDetail'));
const RecurringEditor = lazy(() => import('./pages/recurring/RecurringEditor'));
const DocumentEditor = lazy(() => import('./pages/DocumentEditor'));
const Bills = lazy(() => import('./pages/Bills'));
const Quotes = lazy(() => import('./pages/Quotes'));
const QuoteDetail = lazy(() => import('./pages/QuoteDetail'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const PurchaseOrderDetail = lazy(() => import('./pages/PurchaseOrderDetail'));
const BillDetail = lazy(() => import('./pages/BillDetail'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const Items = lazy(() => import('./pages/Items'));
const Banking = lazy(() => import('./pages/Banking'));
const AddBankAccounts = lazy(() => import('./pages/banking/AddBankAccounts'));
const Reconcile = lazy(() => import('./pages/Reconcile'));
const Accounts = lazy(() => import('./pages/Accounts'));
const JournalNew = lazy(() => import('./pages/JournalNew'));
const Journals = lazy(() => import('./pages/Journals'));
const JournalEntry = lazy(() => import('./pages/JournalDetail'));
const Reports = lazy(() => import('./pages/Reports'));
const Report = lazy(() => import('./pages/Report'));
const Settings = lazy(() => import('./pages/Settings'));
const Account = lazy(() => import('./pages/Account'));
const GroupReport = lazy(() => import('./pages/groups/GroupReport'));
const GroupSetup = lazy(() => import('./pages/groups/GroupSetup'));
const AcceptInvite = lazy(() => import('./pages/team/AcceptInvite'));
const CreditList = lazy(() => import('./pages/credits/CreditList').then((m) => ({ default: m.CreditList })));
const CreditDetail = lazy(() => import('./pages/credits/CreditDetail').then((m) => ({ default: m.CreditDetailPage })));
const PaymentList = lazy(() => import('./pages/payments/PaymentList').then((m) => ({ default: m.PaymentList })));
const MyExpenses = lazy(() => import('./pages/expenses/MyExpenses'));
const ExpenseEditor = lazy(() => import('./pages/expenses/ExpenseEditor'));
const ExpenseDetail = lazy(() => import('./pages/expenses/ExpenseDetail'));
const ExpenseReview = lazy(() => import('./pages/expenses/Review'));
const Reimburse = lazy(() => import('./pages/expenses/Reimburse'));
const TripPage = lazy(() => import('./pages/expenses/TripPage'));
const TravelRates = lazy(() => import('./pages/expenses/TravelRates'));
const Vault = lazy(() => import('./pages/vault/Vault'));
const VaultDocumentPage = lazy(() => import('./pages/vault/VaultDocumentPage'));
const FixedAssetList = lazy(() => import('./pages/fixedassets/FixedAssetList'));
const FixedAssetDetail = lazy(() => import('./pages/fixedassets/FixedAssetDetail'));
const FixedAssetEditor = lazy(() => import('./pages/fixedassets/FixedAssetEditor'));
const FixedAssetSettings = lazy(() => import('./pages/fixedassets/FixedAssetSettings'));
const PracticeShell = lazy(() => import('./pages/practice/PracticeShell').then((m) => ({ default: m.PracticeShell })));
const PracticeClients = lazy(() => import('./pages/practice/Clients'));
const PracticeClientPage = lazy(() => import('./pages/practice/ClientPage'));
const PracticeQuestions = lazy(() => import('./pages/practice/PracticeQuestions'));
const PracticeDocuments = lazy(() => import('./pages/practice/PracticeQuestions').then((m) => ({ default: m.PracticeDocuments })));
const PracticeDeadlines = lazy(() => import('./pages/practice/deadlines'));
const PracticeFees = lazy(() => import('./pages/practice/PracticeFees'));
const PracticeTeam = lazy(() => import('./pages/practice/PracticeTeam'));
const PracticeSettings = lazy(() => import('./pages/practice/PracticeSettings'));
const AcceptPracticeInvite = lazy(() => import('./pages/practice/AcceptPracticeInvite'));
const AcceptAccountantInvite = lazy(() => import('./pages/practice/AcceptAccountantInvite'));
const FromYourAccountant = lazy(() => import('./pages/accountant/FromYourAccountant'));
const PaymentDetail = lazy(() => import('./pages/payments/PaymentDetail').then((m) => ({ default: m.PaymentDetailPage })));

/** An old address after a rename (vendors became suppliers): the same page, query and hash at the new one. */
function RenamedPath({ from, to }: { from: string; to: string }) {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${to}${pathname.slice(from.length)}${search}${hash}`} replace />;
}

function NotFound() {
  return <PageHead title="Page not found" sub={`That page does not exist, or it is not part of ${APP_NAME} yet.`} crumb={{ to: '/', label: 'Dashboard' }} />;
}

export function App() {
  const session = useSession();
  // An invitation link, kept through signing in or creating an account (auth/invite.ts).
  const [invite, setInvite] = useState(() => pendingInviteToken());
  const inviteDone = useCallback(() => setInvite(null), []);
  // A subscription paid for at Stripe, kept through signing in or creating an account (auth/checkout.ts).
  const [checkout, setCheckout] = useState(() => pendingCheckout());
  const checkoutDone = useCallback(() => setCheckout(null), []);
  // An invitation to a practice (ADR 0043), kept the same way.
  const [practiceInvite, setPracticeInvite] = useState(() => pendingPracticeInviteToken());
  const practiceInviteDone = useCallback(() => setPracticeInvite(null), []);
  // A business's invitation to its accountant (ADR 0044): practice onboarding, then accepting.
  const [accountantInvite, setAccountantInvite] = useState(() => pendingAccountantInviteToken());
  const accountantInviteDone = useCallback(() => setAccountantInvite(null), []);
  if (session.status === 'loading') return <div className="auth-page"><Loading label="Signing you in" /></div>;
  if (session.status === 'signed-out') return <SignIn invited={!!invite || !!practiceInvite || !!accountantInvite} checkout={checkout} accountantInvite={accountantInvite} />;
  if (invite) return <Suspense fallback={<div className="auth-page"><Loading /></div>}><AcceptInvite token={invite} onDone={inviteDone} /></Suspense>;
  if (practiceInvite) return <Suspense fallback={<div className="auth-page"><Loading /></div>}><AcceptPracticeInvite token={practiceInvite} onDone={practiceInviteDone} /></Suspense>;
  if (accountantInvite) return <Suspense fallback={<div className="auth-page"><Loading /></div>}><AcceptAccountantInvite token={accountantInvite} onDone={accountantInviteDone} /></Suspense>;
  if (checkout) return <LinkCheckout id={checkout} onDone={checkoutDone} />;
  return <BrowserRouter><Workspace /></BrowserRouter>;
}

/** Whether this tab has landed yet: a practice member lands on Clients once, then goes where they choose. */
const LANDED_KEY = 'mybooks.landed';
function firstLanding(): boolean {
  try {
    if (sessionStorage.getItem(LANDED_KEY)) return false;
    sessionStorage.setItem(LANDED_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/** The practice (under /practice) or a company: the person's own, or a client's books. */
function Workspace() {
  const session = useSession();
  const location = useLocation();
  const [landing, setLanding] = useState(() => firstLanding());
  // The redirect to Clients is for the first screen only; later visits to "/" (Practice books) must stay.
  useEffect(() => { setLanding(false); }, []);
  const inPractice = location.pathname === '/practice' || location.pathname.startsWith('/practice/');
  // Practice members land on the practice's Clients page (PRACTICE-DESIGN.md 4).
  if (landing && location.pathname === '/' && session.practices.length > 0 && !session.client) return <Navigate to="/practice" replace />;
  if (inPractice) {
    if (session.practices.length === 0) return <Navigate to="/" replace />;
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/practice" element={<PracticeShell />}>
            <Route index element={<S><PracticeClients /></S>} />
            <Route path="clients/:linkId" element={<S><PracticeClientPage /></S>} />
            <Route path="questions" element={<S><PracticeQuestions /></S>} />
            <Route path="deadlines" element={<S><PracticeDeadlines /></S>} />
            <Route path="documents" element={<S><PracticeDocuments /></S>} />
            <Route path="fees" element={<S><PracticeFees /></S>} />
            <Route path="team" element={<S><PracticeTeam /></S>} />
            <Route path="settings" element={<S><PracticeSettings /></S>} />
            <Route path="*" element={<Navigate to="/practice" replace />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }
  // Someone who is only in a practice has no company of their own to show.
  if (!session.tenant) return session.practices.length > 0 ? <Navigate to="/practice" replace /> : <FirstBusiness />;
  return <CompanyRoutes />;
}

function CompanyRoutes() {
  const session = useSession();
  if (!session.tenant) return <FirstBusiness />;
  // A business is used only while one of its owners has a plan in force (ADR 0040).
  if (!session.tenant.planInForce) return <PlanRequired />;
  const role = session.tenant.role;
  const expenseRoutes = (
    <>
      <Route path="expenses" element={<S><MyExpenses /></S>} />
      <Route path="expenses/new" element={<S><ExpenseEditor /></S>} />
      <Route path="expenses/:id" element={<S><ExpenseDetail /></S>} />
      <Route path="expenses/:id/edit" element={<S><ExpenseEditor /></S>} />
      <Route path="expenses/trips/new" element={<S><TripPage /></S>} />
      <Route path="expenses/trips/:id" element={<S><TripPage /></S>} />
    </>
  );
  // An employee has only their own expenses (ADR 0023); every other address leads back to them.
  if (isEmployee(role)) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route element={<Shell key={session.tenant.id} />}>
            <Route index element={<Navigate to="/expenses" replace />} />
            {expenseRoutes}
            <Route path="*" element={<Navigate to="/expenses" replace />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }
  return (
    <Suspense fallback={null}>
        <Routes>
          {/* Keyed by business so every screen starts fresh after switching. */}
          <Route element={<Shell key={session.tenant.id} />}>
            <Route index element={<S><Dashboard /></S>} />
            <Route path="invoices" element={<S><Invoices /></S>} />
            <Route path="invoices/new" element={<S><DocumentEditor kind="invoice" /></S>} />
            <Route path="invoices/:id" element={<S><InvoiceDetail /></S>} />
            <Route path="invoices/:id/edit" element={<S><DocumentEditor kind="invoice" /></S>} />
            <Route path="recurring-invoices" element={<S><RecurringList /></S>} />
            <Route path="recurring-invoices/new" element={<S><RecurringEditor /></S>} />
            <Route path="recurring-invoices/:id" element={<S><RecurringDetail /></S>} />
            <Route path="recurring-invoices/:id/edit" element={<S><RecurringEditor /></S>} />
            <Route path="quotes" element={<S><Quotes /></S>} />
            <Route path="quotes/new" element={<S><DocumentEditor kind="quote" /></S>} />
            <Route path="quotes/:id" element={<S><QuoteDetail /></S>} />
            <Route path="quotes/:id/edit" element={<S><DocumentEditor kind="quote" /></S>} />
            <Route path="purchase-orders" element={<S><PurchaseOrders /></S>} />
            <Route path="purchase-orders/new" element={<S><DocumentEditor kind="purchase_order" /></S>} />
            <Route path="purchase-orders/:id" element={<S><PurchaseOrderDetail /></S>} />
            <Route path="purchase-orders/:id/edit" element={<S><DocumentEditor kind="purchase_order" /></S>} />
            <Route path="bills" element={<S><Bills /></S>} />
            <Route path="bills/new" element={<S><DocumentEditor kind="bill" /></S>} />
            <Route path="bills/:id" element={<S><BillDetail /></S>} />
            <Route path="bills/:id/edit" element={<S><DocumentEditor kind="bill" /></S>} />
            <Route path="credit-notes" element={<S><CreditList kind="credit_note" /></S>} />
            <Route path="credit-notes/new" element={<S><DocumentEditor kind="credit_note" /></S>} />
            <Route path="credit-notes/:id" element={<S><CreditDetail kind="credit_note" /></S>} />
            <Route path="credit-notes/:id/edit" element={<S><DocumentEditor kind="credit_note" /></S>} />
            <Route path="supplier-credits" element={<S><CreditList kind="vendor_credit" /></S>} />
            <Route path="supplier-credits/new" element={<S><DocumentEditor kind="vendor_credit" /></S>} />
            <Route path="supplier-credits/:id" element={<S><CreditDetail kind="vendor_credit" /></S>} />
            <Route path="supplier-credits/:id/edit" element={<S><DocumentEditor kind="vendor_credit" /></S>} />
            {/* Vendors were renamed suppliers; links to the old addresses still work. */}
            <Route path="vendor-credits/*" element={<RenamedPath from="/vendor-credits" to="/supplier-credits" />} />
            <Route path="payments-received" element={<S><PaymentList side="sales" /></S>} />
            <Route path="payments-received/:id" element={<S><PaymentDetail kind="received" /></S>} />
            <Route path="payments-made" element={<S><PaymentList side="purchases" /></S>} />
            <Route path="payments-made/:id" element={<S><PaymentDetail kind="made" /></S>} />
            <Route path="customer-refunds" element={<Navigate to="/payments-received?view=refunds" replace />} />
            <Route path="customer-refunds/:id" element={<S><PaymentDetail kind="customer_refund" /></S>} />
            <Route path="supplier-refunds" element={<Navigate to="/payments-made?view=refunds" replace />} />
            <Route path="supplier-refunds/:id" element={<S><PaymentDetail kind="vendor_refund" /></S>} />
            <Route path="vendor-refunds/*" element={<RenamedPath from="/vendor-refunds" to="/supplier-refunds" />} />
            <Route path="customers" element={<S><Contacts type="customer" /></S>} />
            <Route path="suppliers" element={<S><Contacts type="vendor" /></S>} />
            <Route path="vendors" element={<RenamedPath from="/vendors" to="/suppliers" />} />
            <Route path="contacts/:id" element={<S><ContactDetail /></S>} />
            <Route path="items" element={<S><Items /></S>} />
            <Route path="banking" element={<S><Banking /></S>} />
            <Route path="banking/add" element={<S><AddBankAccounts /></S>} />
            <Route path="reconcile" element={<S><Reconcile /></S>} />
            <Route path="accounts" element={<S><Accounts /></S>} />
            <Route path="journals" element={<S><Journals /></S>} />
            <Route path="journals/new" element={<S><JournalNew /></S>} />
            <Route path="journals/:id" element={<S><JournalEntry /></S>} />
            {/* The fixed asset register (ADR 0052); not in the employee's routes above. */}
            <Route path="fixed-assets" element={<S><FixedAssetList /></S>} />
            <Route path="fixed-assets/new" element={<S><FixedAssetEditor key="new" /></S>} />
            <Route path="fixed-assets/settings" element={<S><FixedAssetSettings /></S>} />
            <Route path="fixed-assets/:id" element={<S><FixedAssetDetail /></S>} />
            <Route path="fixed-assets/:id/edit" element={<S><FixedAssetEditor /></S>} />
            <Route path="reports" element={<S><Reports /></S>} />
            <Route path="reports/group" element={<S><GroupReport /></S>} />
            <Route path="reports/:key" element={<S><Report /></S>} />
            <Route path="groups/new" element={<S><GroupSetup /></S>} />
            <Route path="groups/:id" element={<S><GroupSetup /></S>} />
            <Route path="settings" element={<S><Settings /></S>} />
            <Route path="account" element={<S><Account /></S>} />
            <Route path="from-your-accountant" element={<S><FromYourAccountant /></S>} />
            {/* The document vault (ADR 0031); not in the employee's routes above. */}
            <Route path="vault" element={<S><Vault /></S>} />
            <Route path="vault/:id" element={<S><VaultDocumentPage /></S>} />
            {expenseRoutes}
            {canApproveExpenses(role) && <Route path="expenses/review" element={<S><ExpenseReview /></S>} />}
            {canApproveExpenses(role) && <Route path="expenses/reimburse" element={<S><Reimburse /></S>} />}
            {canApproveExpenses(role) && <Route path="expenses/rates" element={<S><TravelRates /></S>} />}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
    </Suspense>
  );
}

/** Each page loads its code on first visit. */
function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}
